import { LoadingAddon } from '../../primitives/loading-addon';
import { cn } from '../../utils/cn';
import { CalendarDatePickerV2, MEXICO_TIMEZONE } from '../calendar/calendar-date-picker-v2';
import { LabelWithoutRef } from '../label/label-without-ref';

interface ScheduleRange {
  from?: number;
  to?: number;
  [key: string]: unknown;
}

interface ScheduleStartEndProps {
  disabled?: boolean;
  id?: string;
  isLoading?: boolean;
  range: ScheduleRange | undefined;
  onChange: (range: ScheduleRange | null | undefined) => void;
}

export function ScheduleStartEnd({
  disabled = false,
  id,
  isLoading = false,
  range,
  onChange,
}: ScheduleStartEndProps) {
  const startDate = range?.from;
  const endDate = range?.to;

  function emitChange(from?: number, to?: number) {
    if (!from && !to) {
      // Keep an explicit empty value to avoid RHF falling back to default values.
      onChange(null);
    } else if (from && !to) {
      const { to: _, ...rest } = range ?? {};
      onChange({ ...rest, from });
    } else if (!from && to) {
      const { from: _, ...rest } = range ?? {};
      onChange({ ...rest, to });
    } else {
      onChange({ ...(range ?? {}), from, to });
    }
  }

  return (
    <div
      className={cn(
        'relative flex flex-wrap gap-4 rounded-3xl border border-input p-4 pt-3 shadow-xs',
        {
          'pointer-events-none border-input/50': disabled || isLoading,
        },
      )}
    >
      <div className="flex-1 space-y-1">
        <LabelWithoutRef>Fecha de inicio</LabelWithoutRef>
        <CalendarDatePickerV2
          button={{
            text: 'Limpiar',
            id: id ? `${id}-startDate-button` : undefined,
            onClick: () => {
              emitChange(undefined, endDate);
            },
          }}
          date={startDate ? new Date(startDate) : undefined}
          fromDate={new Date()}
          hasTime
          id={id ? `${id}-startDate` : undefined}
          mode="single"
          onChange={(day) => {
            const from = day?.getTime();
            emitChange(from, endDate);
          }}
          timeZone={MEXICO_TIMEZONE}
        />
      </div>
      <div className="flex-1 space-y-1">
        <LabelWithoutRef>Fecha de fin</LabelWithoutRef>
        <CalendarDatePickerV2
          button={{
            text: 'Limpiar',
            id: id ? `${id}-endDate-button` : undefined,
            onClick: () => {
              emitChange(startDate, undefined);
            },
          }}
          defaultStartTime="23:59"
          date={endDate ? new Date(endDate) : undefined}
          fromDate={startDate ? new Date(startDate) : new Date()}
          hasTime
          id={id ? `${id}-endDate` : undefined}
          mode="single"
          onChange={(day) => {
            const to = day?.getTime();
            emitChange(startDate, to);
          }}
          timeZone={MEXICO_TIMEZONE}
        />
      </div>
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
}
